import React, { useState, useRef, useCallback } from 'react';
import { WorkflowNode } from './WorkflowNode';
import { WorkflowCanvasState, WorkflowNode as WorkflowNodeType, WorkflowConnection, NodeTemplate } from '../../types/workflow';

interface WorkflowCanvasProps {
  canvasState: WorkflowCanvasState;
  onUpdateCanvasState: (updates: Partial<WorkflowCanvasState>) => void;
  onUpdateNode: (nodeId: string, updates: Partial<WorkflowNodeType>) => void;
  onDeleteNode: (nodeId: string) => void;
  onAddConnection: (connection: WorkflowConnection) => void;
  onDeleteConnection: (connectionId: string) => void;
  onAddNode: (template: NodeTemplate, position?: { x: number; y: number }) => void;
}

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  canvasState,
  onUpdateCanvasState,
  onUpdateNode,
  onDeleteNode,
  onAddConnection,
  onDeleteConnection,
  onAddNode,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [nodeDragDelta, setNodeDragDelta] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [connectionPreview, setConnectionPreview] = useState<{ x: number; y: number } | null>(null);
  const [hoverInput, setHoverInput] = useState<{ nodeId: string; inputId: string; rect: DOMRect } | null>(null);
  const NODE_WIDTH = 192; // must mirror node width in UI
  const NODE_HEIGHT = 200; // approximate height for clamping

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      onUpdateCanvasState({
        selectedNodeId: undefined,
        selectedConnectionId: undefined,
        isConnecting: false,
        connectionStart: undefined,
      });
    }
  }, [onUpdateCanvasState]);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - canvasOffset.x, y: e.clientY - canvasOffset.y });
    }
  }, [canvasOffset]);

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setCanvasOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
    // Node dragging
    if (draggingNodeId) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left) / zoom - nodeDragDelta.x;
      const y = (e.clientY - rect.top) / zoom - nodeDragDelta.y;
      onUpdateNode(draggingNodeId, {
        position: {
          x: Math.max(0, x),
          y: Math.max(0, y),
        },
      });
    }
    if (canvasState.isConnecting) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left) / zoom;
      const y = (e.clientY - rect.top) / zoom;
      setConnectionPreview({ x, y });
    }
  }, [isDragging, dragStart, draggingNodeId, nodeDragDelta.x, nodeDragDelta.y, zoom, onUpdateNode, canvasState.isConnecting]);

  const handleCanvasMouseUp = useCallback(() => {
    setIsDragging(false);
    setDraggingNodeId(null);
  }, []);

  const handleCanvasDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    console.log('Canvas drop event triggered');
    
    try {
      // Try preferred MIME type first, then fallback to text/plain for Safari
      const templateData =
        e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
      console.log('Retrieved template data:', templateData);
      
      let template: NodeTemplate | undefined;
      if (templateData) {
        template = JSON.parse(templateData);
        console.log('Parsed template:', template);
      }

      // Fallback template if parsing failed or data missing
      if (!template) {
        template = {
          type: 'ai-agent' as any,
          name: 'Node',
          description: 'Dropped node',
          icon: '🔧',
          category: 'utilities',
          color: '#3b82f6',
          inputs: [],
          outputs: [],
          defaultData: {},
        };
      }

      if (template) {
        const rect = canvasRef.current?.getBoundingClientRect();
        console.log('Canvas rect:', rect);
        
        if (rect) {
          // rect already includes the applied CSS transform (translate/scale),
          // so subtracting canvasOffset again would double-count it.
          let x = (e.clientX - rect.left) / zoom;
          let y = (e.clientY - rect.top) / zoom;

          // Clamp to keep node fully within canvas visible bounds
          const unscaledWidth = rect.width / zoom;
          const unscaledHeight = rect.height / zoom;
          x = Math.max(0, Math.min(unscaledWidth - NODE_WIDTH, x));
          y = Math.max(0, Math.min(unscaledHeight - NODE_HEIGHT, y));

          console.log('Calculated clamped position:', { x, y });
          onAddNode(template, { x, y });
        }
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  }, [zoom, onAddNode]);

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
    console.log('Drag over canvas');
  }, []);

  const handleCanvasDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.1, Math.min(3, prev * delta)));
  }, []);

  const handleNodeSelect = useCallback((nodeId: string) => {
    onUpdateCanvasState({ selectedNodeId: nodeId });
  }, [onUpdateCanvasState]);

  const handleBeginDragNode = useCallback((nodeId: string, e: React.MouseEvent) => {
    // Only start dragging when not interacting with connection dots
    setDraggingNodeId(nodeId);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const node = canvasState.nodes.find(n => n.id === nodeId);
    if (!node) return;
    const cursorX = (e.clientX - rect.left) / zoom;
    const cursorY = (e.clientY - rect.top) / zoom;
    setNodeDragDelta({ x: cursorX - node.position.x, y: cursorY - node.position.y });
  }, [canvasState.nodes, zoom]);

  const handleStartConnection = useCallback((nodeId: string, outputId: string) => {
    onUpdateCanvasState({
      isConnecting: true,
      connectionStart: { nodeId, outputId },
    });
    setConnectionPreview(null);
  }, [onUpdateCanvasState]);

  const handleEndConnection = useCallback((nodeId: string, inputId: string) => {
    if (canvasState.isConnecting && canvasState.connectionStart) {
      const connection: WorkflowConnection = {
        id: `conn-${Date.now()}`,
        sourceNodeId: canvasState.connectionStart.nodeId,
        sourceOutputId: canvasState.connectionStart.outputId,
        targetNodeId: nodeId,
        targetInputId: inputId,
      };
      onAddConnection(connection);
    }
    onUpdateCanvasState({
      isConnecting: false,
      connectionStart: undefined,
    });
    setConnectionPreview(null);
    setHoverInput(null);
  }, [canvasState.isConnecting, canvasState.connectionStart, onAddConnection, onUpdateCanvasState]);

  const renderConnection = (connection: WorkflowConnection) => {
    const sourceNode = canvasState.nodes.find(n => n.id === connection.sourceNodeId);
    const targetNode = canvasState.nodes.find(n => n.id === connection.targetNodeId);
    
    if (!sourceNode || !targetNode) {
      return null;
    }

    // Calculate connection points more accurately
    const sourceX = sourceNode.position.x + 192; // Node width (192px)
    const sourceY = sourceNode.position.y + 100; // Center of node height
    const targetX = targetNode.position.x;
    const targetY = targetNode.position.y + 100; // Center of node height

    // Calculate line properties
    const deltaX = targetX - sourceX;
    const deltaY = targetY - sourceY;
    const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;

    return (
      <div
        key={connection.id}
        className="absolute pointer-events-auto cursor-pointer hover:opacity-80 transition-opacity"
        style={{ 
          zIndex: 10,
          left: `${sourceX}px`,
          top: `${sourceY}px`,
          width: `${length}px`,
          height: '4px',
          backgroundColor: '#3b82f6',
          transformOrigin: '0 50%',
          transform: `rotate(${angle}deg)`,
          borderRadius: '2px',
          boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
        }}
        onClick={() => onDeleteConnection(connection.id)}
        title={`Connection: ${sourceNode.name} → ${targetNode.name}`}
      >
        {/* Arrow head */}
        <div
          style={{
            position: 'absolute',
            right: '-6px',
            top: '-3px',
            width: '0',
            height: '0',
            borderLeft: '6px solid #3b82f6',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent'
          }}
        />
      </div>
    );
  };

  const renderPreviewConnection = () => {
    if (!canvasState.isConnecting || !canvasState.connectionStart || !connectionPreview) {
      return null;
    }
    const sourceNode = canvasState.nodes.find(n => n.id === canvasState.connectionStart!.nodeId);
    if (!sourceNode) return null;

    const sourceX = sourceNode.position.x + 192;
    const sourceY = sourceNode.position.y + 100;
    let targetX = connectionPreview.x;
    let targetY = connectionPreview.y;

    // Snap to hovered input port center
    if (hoverInput) {
      // Convert screen-space rect center to canvas-space using current zoom and canvas DOMRect
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (canvasRect) {
        const centerX = hoverInput.rect.left + hoverInput.rect.width / 2;
        const centerY = hoverInput.rect.top + hoverInput.rect.height / 2;
        targetX = (centerX - canvasRect.left) / zoom;
        targetY = (centerY - canvasRect.top) / zoom;
      }
    }

    // Magnetic snap to closest input even if not precisely hovered
    if (!hoverInput) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      const snapRadius = 28 / zoom; // px in canvas space
      if (canvasRect) {
        let bestDist = Infinity;
        let bestPoint: { x: number; y: number } | null = null;
        for (const node of canvasState.nodes) {
          // approximate input port near right edge middle of the node
          const ix = node.position.x + 192;
          const iy = node.position.y + 100;
          const dx = ix - targetX;
          const dy = iy - targetY;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < bestDist) {
            bestDist = d;
            bestPoint = { x: ix, y: iy };
          }
        }
        if (bestPoint && bestDist <= snapRadius) {
          targetX = bestPoint.x;
          targetY = bestPoint.y;
        }
      }
    }

    const deltaX = targetX - sourceX;
    const deltaY = targetY - sourceY;
    const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;

    return (
      <div
        className="absolute pointer-events-none opacity-80"
        style={{
          zIndex: 15,
          left: `${sourceX}px`,
          top: `${sourceY}px`,
          width: `${length}px`,
          height: '2px',
          background: 'repeating-linear-gradient(90deg,#60a5fa 0 8px,transparent 8px 14px)',
          transformOrigin: '0 50%',
          transform: `rotate(${angle}deg)`,
          borderRadius: '2px',
        }}
      />
    );
  };

  return (
    <div
      className="flex-1 relative overflow-hidden bg-gray-50"
      style={{ minHeight: '600px', height: '100%' }}
      onDrop={handleCanvasDrop}
      onDragOver={handleCanvasDragOver}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
    >
      {/* Canvas Controls */}
      <div className="absolute top-4 right-4 z-30 flex space-x-2">
        <button
          onClick={() => setZoom(1)}
          className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          Reset Zoom
        </button>
        <div className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200">
          <span className="text-sm text-gray-600">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="w-full h-full relative cursor-grab active:cursor-grabbing"
        onClick={handleCanvasClick}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
        onWheel={handleWheel}
        style={{
          minHeight: '100%',
          transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
      >
        {/* Grid Background */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(to right, #e5e7eb 1px, transparent 1px),
              linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
          }}
        />

        {/* Drop Zone Indicator */}
        {isDragOver && (
          <div className="absolute inset-0 bg-blue-50/50 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center pointer-events-none" style={{ zIndex: 20 }}>
            <div className="text-center">
              <div className="text-4xl mb-2">📦</div>
              <p className="text-lg font-semibold text-blue-600">Drop node here</p>
              <p className="text-sm text-blue-500">Release to add to workflow</p>
            </div>
          </div>
        )}

        {/* Nodes */}

        {/* Nodes */}
        {canvasState.nodes.map((node) => (
          <WorkflowNode
            key={node.id}
            node={node}
            isSelected={canvasState.selectedNodeId === node.id}
            isConnecting={canvasState.isConnecting}
            onSelect={handleNodeSelect}
            onStartConnection={handleStartConnection}
            onEndConnection={handleEndConnection}
            onUpdateNode={onUpdateNode}
            onDeleteNode={onDeleteNode}
            onBeginDrag={handleBeginDragNode}
            onHoverInput={(nodeId, inputId, rect) => setHoverInput({ nodeId, inputId, rect })}
            onLeaveInput={() => setHoverInput(null)}
          />
        ))}

        {/* Connections - Render after nodes so they appear on top */}
        {canvasState.connections.map(renderConnection)}
        {renderPreviewConnection()}
      </div>

      {/* Canvas Info */}
      <div className="absolute bottom-4 left-4 z-30">
        <div className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200">
          <span className="text-sm text-gray-600">
            Nodes: {canvasState.nodes.length} | Connections: {canvasState.connections.length}
          </span>
        </div>
      </div>
    </div>
  );
};